"use client";

import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ClipboardList, Pencil, Plus, ShieldCheck, UserCog } from "lucide-react";

import type { DeviceServiceRecordDto } from "@/lib/api/types";
import { Alert, Button, Card, EmptyState, Input, Modal, Skeleton, ThemedSelect, type ThemedSelectOption } from "@/components/ui-system";
import {
  useCreateDeviceServiceRecordMutation,
  useDeviceServiceRecordPlatformUsers,
  useDeviceServiceRecords,
  useUpdateDeviceServiceRecordMutation,
} from "@/modules/devices/hooks";

type DeviceServiceRecordsProps = {
  deviceId: string;
  canManage: boolean;
  onCalibrationRecorded?: (nextCalibrationDate: string) => void;
};

type ServiceFormState = {
  serviceDate: string;
  serviceType: string;
  description: string;
  performedByType: "platform_user" | "manual";
  performedByUserId: string;
  performedByName: string;
  nextCalibrationDate: string;
};

const SERVICE_TYPE_OPTIONS: ThemedSelectOption[] = [
  { value: "inspection", label: "Inspection" },
  { value: "repair", label: "Repair" },
  { value: "calibration", label: "Calibration" },
  { value: "replacement", label: "Replacement" },
  { value: "cleaning", label: "Cleaning" },
  { value: "firmware_support", label: "Firmware support" },
  { value: "other", label: "Other" },
];

const PERFORMED_BY_OPTIONS: ThemedSelectOption[] = [
  { value: "platform_user", label: "Platform user" },
  { value: "manual", label: "Manual entry" },
];

function todayDateInput(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function initialFormState(): ServiceFormState {
  return {
    serviceDate: todayDateInput(),
    serviceType: "inspection",
    description: "",
    performedByType: "platform_user",
    performedByUserId: "",
    performedByName: "",
    nextCalibrationDate: "",
  };
}

function toFormState(record: DeviceServiceRecordDto): ServiceFormState {
  return {
    serviceDate: record.service_date,
    serviceType: record.service_type,
    description: record.description,
    performedByType: record.performed_by_type,
    performedByUserId: record.performed_by_user_id ?? "",
    performedByName: record.performed_by_type === "manual" ? record.performed_by_name ?? "" : "",
    nextCalibrationDate: record.next_calibration_date ?? "",
  };
}

function humanizeServiceType(value: string): string {
  const found = SERVICE_TYPE_OPTIONS.find((option) => option.value === value);
  if (found) {
    return found.label;
  }
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString();
}

function formatTimestampLabel(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function normalizeErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "Could not save the service record.";
}

export function DeviceServiceRecords({
  deviceId,
  canManage,
  onCalibrationRecorded,
}: DeviceServiceRecordsProps) {
  const recordsQuery = useDeviceServiceRecords(deviceId, Boolean(deviceId));
  const createMutation = useCreateDeviceServiceRecordMutation(deviceId);
  const updateMutation = useUpdateDeviceServiceRecordMutation(deviceId);

  const [open, setOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DeviceServiceRecordDto | null>(null);
  const [form, setForm] = useState<ServiceFormState>(() => initialFormState());
  const [formError, setFormError] = useState<string | null>(null);

  const platformUsersQuery = useDeviceServiceRecordPlatformUsers(deviceId, canManage && open);
  const platformUserOptions = useMemo<ThemedSelectOption[]>(
    () =>
      (platformUsersQuery.data?.items ?? []).map((user) => ({
        value: user.id,
        label: user.name?.trim() || user.email,
        description: user.email,
      })),
    [platformUsersQuery.data?.items],
  );

  const submitPending = createMutation.isPending || updateMutation.isPending;
  const isCalibration = form.serviceType === "calibration";

  useEffect(() => {
    if (!open) {
      setForm(initialFormState());
      setEditingRecord(null);
      setFormError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || form.performedByType !== "platform_user") {
      return;
    }
    if (form.performedByUserId || platformUserOptions.length === 0) {
      return;
    }
    setForm((current) => ({
      ...current,
      performedByUserId: platformUserOptions[0]?.value ?? "",
    }));
  }, [form.performedByType, form.performedByUserId, open, platformUserOptions]);

  const openCreateModal = () => {
    setEditingRecord(null);
    setForm(initialFormState());
    setOpen(true);
  };

  const openEditModal = (record: DeviceServiceRecordDto) => {
    setEditingRecord(record);
    setForm(toFormState(record));
    setFormError(null);
    setOpen(true);
  };

  const validateForm = (): string | null => {
    if (!form.serviceDate) {
      return "Choose a service date.";
    }
    if (!form.serviceType) {
      return "Choose a service type.";
    }
    if (!form.description.trim()) {
      return "Enter a short service note.";
    }
    if (form.performedByType === "platform_user" && !form.performedByUserId) {
      return "Choose the platform user who completed the work.";
    }
    if (form.performedByType === "manual" && !form.performedByName.trim()) {
      return "Enter who completed the work.";
    }
    if (isCalibration && !form.nextCalibrationDate) {
      return "Choose the next calibration date.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError(null);
    const payload = {
      service_date: form.serviceDate,
      service_type: form.serviceType,
      description: form.description.trim(),
      performed_by_type: form.performedByType,
      performed_by_user_id: form.performedByType === "platform_user" ? form.performedByUserId : null,
      performed_by_name: form.performedByType === "manual" ? form.performedByName.trim() : null,
      next_calibration_date: isCalibration ? form.nextCalibrationDate : null,
    } as const;

    try {
      const saved = editingRecord
        ? await updateMutation.mutateAsync({ recordId: editingRecord.id, body: payload })
        : await createMutation.mutateAsync(payload);
      if (saved.service_type === "calibration" && saved.next_calibration_date) {
        onCalibrationRecorded?.(saved.next_calibration_date);
      }
      setOpen(false);
    } catch (error) {
      setFormError(normalizeErrorMessage(error));
    }
  };

  return (
    <>
      <Card className="border-border/60 bg-card p-ds-5 shadow-card">
        <div className="flex flex-col gap-ds-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-ds-2">
            <div className="flex items-center gap-ds-2">
              <ClipboardList className="h-4 w-4 text-primary" aria-hidden />
              <h3 className="text-title text-foreground">Service records</h3>
            </div>
            <p className="text-caption text-muted">
              Keep a dated maintenance history for this logger. Calibration entries also update the lifecycle calibration date.
            </p>
          </div>
          {canManage ? (
            <Button type="button" size="sm" onClick={openCreateModal}>
              <Plus className="h-4 w-4" aria-hidden />
              Add service record
            </Button>
          ) : null}
        </div>

        {!canManage ? (
          <p className="mt-ds-4 rounded-[var(--radius-lg)] border border-border/60 bg-muted-bg/35 px-ds-4 py-ds-3 text-caption text-muted">
            View-only for this account. Service records can be managed by super admins and permitted platform users.
          </p>
        ) : null}

        <div className="mt-ds-5">
          {recordsQuery.isLoading ? (
            <Skeleton className="h-20 rounded-control" />
          ) : recordsQuery.isError ? (
            <Alert tone="error" title="Service history unavailable" description="Could not load service records." />
          ) : (recordsQuery.data?.length ?? 0) === 0 ? (
            <EmptyState title="No service records yet" description="Add the first entry to start tracking inspections, repairs, and calibrations." />
          ) : (
            <div className="space-y-ds-3">
              {recordsQuery.data?.map((record) => (
                <div
                  key={record.id}
                  className="rounded-[var(--radius-xl)] border border-border/70 bg-muted-bg/20 px-ds-4 py-ds-4"
                >
                  <div className="flex flex-col gap-ds-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-ds-2">
                      <div className="flex flex-wrap items-center gap-ds-2">
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-ds-3 py-ds-1 text-micro font-semibold uppercase tracking-wide text-primary">
                          {humanizeServiceType(record.service_type)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-caption text-muted">
                          <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                          {formatDateLabel(record.service_date)}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-foreground">{record.description}</p>
                      <div className="flex flex-wrap gap-x-ds-5 gap-y-ds-2 text-caption text-muted">
                        <span className="inline-flex items-center gap-1">
                          {record.performed_by_type === "platform_user" ? (
                            <UserCog className="h-3.5 w-3.5" aria-hidden />
                          ) : (
                            <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                          )}
                          {record.performed_by_name || "-"}
                        </span>
                        {record.next_calibration_date ? (
                          <span>Next calibration: {formatDateLabel(record.next_calibration_date)}</span>
                        ) : null}
                        <span>Updated: {formatTimestampLabel(record.updated_at)}</span>
                      </div>
                    </div>
                    {canManage ? (
                      <Button type="button" variant="secondary" size="sm" onClick={() => openEditModal(record)}>
                        <Pencil className="h-4 w-4" aria-hidden />
                        Edit
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editingRecord ? "Edit service record" : "Add service record"}
        description="Use service records to keep a clean maintenance trail for the logger."
        size="lg"
        footer={
          <div className="flex items-center justify-end gap-ds-3">
            {formError ? <p className="mr-auto text-caption text-error">{formError}</p> : null}
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={submitPending}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => void handleSubmit()} disabled={submitPending}>
              {submitPending ? "Saving..." : editingRecord ? "Save changes" : "Save record"}
            </Button>
          </div>
        }
      >
        <div className="grid gap-ds-4 md:grid-cols-2">
          <label className="space-y-ds-2">
            <span className="text-sm font-medium text-foreground">Service date</span>
            <Input
              type="date"
              value={form.serviceDate}
              onChange={(event) => setForm((current) => ({ ...current, serviceDate: event.target.value }))}
            />
          </label>
          <label className="space-y-ds-2">
            <span className="text-sm font-medium text-foreground">Service type</span>
            <ThemedSelect
              value={form.serviceType}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  serviceType: value,
                  nextCalibrationDate: value === "calibration" ? current.nextCalibrationDate : "",
                }))
              }
              options={SERVICE_TYPE_OPTIONS}
            />
          </label>
          <label className="space-y-ds-2 md:col-span-2">
            <span className="text-sm font-medium text-foreground">Description</span>
            <textarea
              className="ui-control min-h-28 w-full resize-y"
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              placeholder="Summarize what was serviced, replaced, checked, or calibrated."
            />
          </label>
          <label className="space-y-ds-2">
            <span className="text-sm font-medium text-foreground">Done by</span>
            <ThemedSelect
              value={form.performedByType}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  performedByType: value as "platform_user" | "manual",
                  performedByUserId: value === "platform_user" ? current.performedByUserId : "",
                  performedByName: value === "manual" ? current.performedByName : "",
                }))
              }
              options={PERFORMED_BY_OPTIONS}
            />
          </label>
          {form.performedByType === "platform_user" ? (
            <label className="space-y-ds-2">
              <span className="text-sm font-medium text-foreground">Platform user</span>
              <ThemedSelect
                value={form.performedByUserId}
                onChange={(value) => setForm((current) => ({ ...current, performedByUserId: value }))}
                options={platformUserOptions}
                placeholder={platformUsersQuery.isLoading ? "Loading users..." : "Select platform user"}
                searchable
                searchPlaceholder="Search platform users"
                disabled={platformUsersQuery.isLoading}
              />
            </label>
          ) : (
            <label className="space-y-ds-2">
              <span className="text-sm font-medium text-foreground">Manual name</span>
              <Input
                value={form.performedByName}
                onChange={(event) => setForm((current) => ({ ...current, performedByName: event.target.value }))}
                placeholder="Engineer name, vendor, or field team"
              />
            </label>
          )}
          {isCalibration ? (
            <label className="space-y-ds-2 md:col-span-2">
              <span className="text-sm font-medium text-foreground">Next calibration</span>
              <Input
                type="date"
                value={form.nextCalibrationDate}
                onChange={(event) => setForm((current) => ({ ...current, nextCalibrationDate: event.target.value }))}
              />
            </label>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
